import { 
    Sheet, 
    SheetContent, 
    Button, 
    Badge, 
    AppImage, 
    ShipmentTracking, 
    type TrackingEvent,
} from '@shopmuchmore/design-system';
import { ArrowLeft, Package, Loader2 } from 'lucide-react';
import type { ShipmentTrackingResponse } from '../orders.types';

interface OrderTrackingSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    activeShipmentNumber: string | null;
    isTrackingLoading: boolean;
    trackingData?: ShipmentTrackingResponse;
    trackingEvents: TrackingEvent[];
}

export function OrderTrackingSheet({
    open,
    onOpenChange,
    activeShipmentNumber,
    isTrackingLoading,
    trackingData,
    trackingEvents
}: OrderTrackingSheetProps) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
                <div className="p-8 border-b border-gray-100">
                    <div className="flex items-center gap-4 mb-4">
                        <button 
                            onClick={() => onOpenChange(false)}
                            className="size-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors"
                        >
                            <ArrowLeft className="size-5" />
                        </button>
                        <div>
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Delivery Check</h2>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tracking #{activeShipmentNumber}</p>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-8 py-10">
                    {isTrackingLoading ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-4">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Tracking...</p>
                        </div>
                    ) : trackingData ? (
                        <div className="space-y-10">
                            {/* Shipment Items Breakdown */}
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Items in this parcel</h5>
                                    <Badge variant="outline" className="text-[10px] bg-gray-50 border-gray-200">
                                        {trackingData.product_details.length} {trackingData.product_details.length === 1 ? 'Item' : 'Items'}
                                    </Badge>
                                </div>
                                <div className="grid gap-3">
                                    {trackingData.product_details.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-4 bg-gray-50/50 hover:bg-gray-50 p-3 rounded-2xl border border-gray-100 transition-colors group">
                                            <div className="size-12 rounded-xl bg-white flex items-center justify-center border border-gray-100 overflow-hidden ring-4 ring-transparent group-hover:ring-white transition-all">
                                                <AppImage src={item.thumbnail} alt={item.product_name} size="full" className="rounded-lg" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-gray-900 truncate tracking-tight">{item.product_name}</p>
                                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-0.5">Quantity: {item.quantity}</p>
                                            </div>
                                            <div className="size-8 rounded-full bg-white flex items-center justify-center text-gray-400 shadow-sm border border-gray-50">
                                                <Package className="size-3.5" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <ShipmentTracking events={trackingEvents} />
                            
                            {trackingData.tracking_url && (
                                <Button asChild className="w-full h-14 rounded-2xl bg-gray-900 text-white font-black mt-10">
                                    <a href={trackingData.tracking_url} target="_blank" rel="noopener noreferrer">
                                        View Full Tracking Map
                                    </a>
                                </Button>
                            )}
                        </div>
                    ) : null}
                </div>
            </SheetContent>
        </Sheet>
    );
}
